import { studyQueryKeys } from '@/config/queryKeys';
import { STUDIES_QUERY_STALE_TIME, STUDIES_QUERY_GC_TIME } from '@/constants';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import type { InstrumentRunDataRow, PaginatedStudiesResponse } from '@/types';
import type { SelectionOption } from '@/types/autocomplete';
import { mapStudiesToOptions } from '@/utils/autocomplete';
import { useQuery } from '@tanstack/react-query';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { SelectionCellRenderer } from './shared/SelectionCellRenderer';

/**
 * Custom cell renderer for Study Name selection with Dialog interface
 * Uses the shared SelectionCellRenderer for consistent UX
 * Supports bulk updating empty study names via checkbox
 */
export const StudyNameCellRenderer: FC<ICellRendererParams<InstrumentRunDataRow>> = props => {
  // Get selected organization from store
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization));

  // Subscribe to the studies cache so the cell re-renders when studies load asynchronously.
  // useQuery (rather than queryClient.getQueryData) ensures cache updates are reflected.
  const { data: studiesData } = useQuery<PaginatedStudiesResponse>({
    queryKey: selectedOrg?.id ? studyQueryKeys.listSearch(selectedOrg.id) : ['disabled-studies-query'],
    queryFn: async () => {
      if (!selectedOrg?.id) return { records: [], total: 0, skip: 0, limit: 0, has_more: false };
      return searchStudies(selectedOrg.id, {
        skip: 0,
        limit: 500,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },
    enabled: !!selectedOrg?.id,
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
    refetchOnMount: false,
  });

  // Convert studies to autocomplete options
  const studyOptions = useMemo<SelectionOption[]>(() => {
    if (!studiesData?.records) return [];
    return mapStudiesToOptions(studiesData.records);
  }, [studiesData]);

  return (
    <SelectionCellRenderer
      {...props}
      applyToEmptyKey="study_apply_to_all_empty"
      dialogTitleKey="study_select_dialog_title"
      emptyHintKey="study_select_empty_hint"
      fieldName="study_name"
      options={studyOptions}
      placeholderKey="study_select_placeholder"
    />
  );
};

StudyNameCellRenderer.displayName = 'StudyNameCellRenderer';

export default StudyNameCellRenderer;
